import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { ChevronDown } from "lucide-react";
import { SmartLink } from "../SmartLink";
import { CoinBadge } from "../CoinBadge";
import { useLivePrices, STABLES, MAJORS, fmtPrice, fmtChange } from "../../hooks/useLivePrices";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtVol = (v) => (!v ? "—" : v >= 1e9 ? `$${(v / 1e9).toFixed(2)}B` : v >= 1e6 ? `$${(v / 1e6).toFixed(2)}M` : `$${Math.round(v).toLocaleString()}`);

const Spark = ({ symbol, down: given }) => {
  const [pts, setPts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/markets/ohlc`, { params: { symbol, interval: "1h" } })
      .then((r) => setPts((r.data.candles || []).slice(-24).map((c) => c.close))).catch(() => {});
  }, [symbol]);
  if (pts.length < 2) return <svg className="az-spark" viewBox="0 0 120 36" />;
  const down = given ?? pts[pts.length - 1] < pts[0];
  const lo = Math.min(...pts), rng = Math.max(...pts) - lo || 1;
  const line = pts.map((v, i) => `${((i / (pts.length - 1)) * 120).toFixed(1)},${(33 - ((v - lo) / rng) * 30).toFixed(1)}`).join(" ");
  return <svg className={`az-spark${down ? " down" : ""}`} viewBox="0 0 120 36" data-testid={`az-spark-${symbol}`}><polyline points={line} /></svg>;
};

const SpotRows = ({ rows }) => rows.map((a) => {
  const down = (a.change || 0) < 0;
  return (
    <tr key={a.symbol} data-testid={`az-mkt-row-${a.symbol}`}>
      <td><div className="az-asset"><CoinBadge logo={a.logo} cls={a.cls} icon={a.icon} symbol={a.symbol} size={32} /><b>{a.symbol}</b><span>{a.name}</span></div></td>
      <td className="az-num">{fmtPrice(a.price)}</td>
      <td className={`az-num ${down ? "az-neg" : "az-pos"}`}>{fmtChange(a.change)}</td>
      <td className="az-num az-hide-sm">{fmtVol(a.volume)}</td>
      <td className="az-hide-sm"><Spark symbol={a.symbol} down={down} /></td>
      <td><SmartLink className="az-trade-btn" href={`/trade?symbol=${a.symbol}_USDT`} data-testid={`az-mkt-trade-${a.symbol}`}>Trade</SmartLink></td>
    </tr>
  );
});

const FuturesRows = ({ rows, logos }) => rows.map((m) => (
  <tr key={m.symbol} data-testid={`az-fut-row-${m.symbol}`}>
    <td><div className="az-asset"><CoinBadge logo={logos[m.symbol]?.logo} cls={logos[m.symbol]?.cls} icon={logos[m.symbol]?.icon} symbol={m.symbol} size={32} /><b>{m.symbol}USDT</b><span>Perpetual</span></div></td>
    <td className="az-num">{fmtPrice(m.mark)}</td>
    <td className="az-num az-pos">{m.max_leverage}x</td>
    <td className="az-num az-hide-sm">{m.funding_rate_pct != null ? `${Number(m.funding_rate_pct).toFixed(4)}%` : "—"}</td>
    <td className="az-hide-sm"><Spark symbol={m.symbol} /></td>
    <td><SmartLink className="az-trade-btn" href={`/futures?symbol=${m.symbol}`} data-testid={`az-fut-trade-${m.symbol}`}>Trade</SmartLink></td>
  </tr>
));

export const AzMarkets = () => {
  const assets = useLivePrices();
  const [tab, setTab] = useState("spot");
  const [fut, setFut] = useState([]);
  const [more, setMore] = useState(false);
  useEffect(() => { axios.get(`${API}/futures/markets`).then((r) => setFut((r.data.markets || r.data || []).filter((m) => m.enabled !== false))).catch(() => {}); }, []);
  const spot = useMemo(() => assets.filter((a) => a.price && !STABLES.includes(a.symbol)).sort((a, b) => (b.volume || 0) - (a.volume || 0)), [assets]);
  const logos = useMemo(() => Object.fromEntries(assets.map((a) => [a.symbol, a])), [assets]);
  const futSorted = useMemo(() => [...fut].sort((a, b) => ((MAJORS.indexOf(a.symbol) + 1 || 99) - (MAJORS.indexOf(b.symbol) + 1 || 99)) || a.symbol.localeCompare(b.symbol)), [fut]);
  const list = tab === "spot" ? spot : futSorted;
  const shown = more ? list : list.slice(0, 6);
  const head = tab === "spot" ? ["Price", "24h change", "24h volume"] : ["Mark price", "Max leverage", "Funding"];
  return (
    <section id="az-markets" className="az-wrap az-section" data-testid="az-markets" style={{ scrollMarginTop: 70 }}>
      <h2>Catch your next<br />trading opportunity</h2>
      <div className="az-tabs">
        {["spot", "futures"].map((t) => (
          <button key={t} className={tab === t ? "on" : ""} onClick={() => { setTab(t); setMore(false); }} data-testid={`az-tab-${t}`}>{t === "spot" ? "Spot" : "Futures"}</button>
        ))}
      </div>
      <div className="az-table-wrap">
        <table className="az-table">
          <thead><tr><th>Pair</th>{head.map((h, i) => <th key={h} className={`az-num${i === 2 ? " az-hide-sm" : ""}`}>{h}</th>)}<th className="az-hide-sm az-num">Chart</th><th /></tr></thead>
          <tbody>{tab === "spot" ? <SpotRows rows={shown} /> : <FuturesRows rows={shown} logos={logos} />}</tbody>
        </table>
      </div>
      <div className="az-mkt-foot">
        <SmartLink className="az-btn az-btn-primary" href={tab === "spot" ? "/trade" : "/futures"} data-testid="az-markets-trade-now">Trade now</SmartLink>
        {list.length > 6 && (
          <button className="az-link" onClick={() => setMore((v) => !v)} data-testid="az-markets-more">
            {more ? "See Less" : "See More"} <ChevronDown size={16} style={{ transform: more ? "rotate(180deg)" : "none" }} />
          </button>
        )}
      </div>
    </section>
  );
};
